import React, { useContext, useEffect, useRef, useState } from 'react';
import useScreenWidth from '@/hooks/useScreenWidth';
import NotificationContext from '@/context/NotificationContext';
import CourseDescriptionContext from '@/context/CourseDescriptionContext';
import StickyNavbar from './StickyNavbar';

type NavbarStub = {
  href: string;
  title: string;
};

const NavHeaderSection = ({
  darkTheme = false,
  isCampusEdgeCourseDescription = false,
  isOnlineDegree = false,
}: {
  darkTheme?: boolean;
  isCampusEdgeCourseDescription?: boolean;
  isOnlineDegree?: boolean;
}) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const [navbarStub, setNavbarStub] = useState<NavbarStub[]>();
  const [scrollDownDirection, setScrollDownDirection] = useState<null | boolean>(null);

  const navbar = useRef<null | HTMLDivElement>(null);
  const sliderRef = useRef<null | HTMLDivElement>(null);
  const pricingRef = useRef<null | HTMLDivElement>(null);
  const { courseDetails } = useContext(CourseDescriptionContext);

  const { setNotificationState } = useContext(NotificationContext);

  const screenWidth = useScreenWidth();
  useEffect(() => {
    const handleScroll = () => {
      const prevScroll = window.scrollY;
      setTimeout(() => {
        if (prevScroll < window.scrollY) {
          setScrollDownDirection(true);
        } else if (prevScroll === window.scrollY) {
          setScrollDownDirection(scrollDownDirection);
        } else {
          setScrollDownDirection(false);
        }
      }, 200);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const resetSlider = () => {
    if (sliderRef.current) {
      const elem = sliderRef.current as HTMLDivElement;

      // Get href array i.e: ["a","b","c","d"]
      const navbarStubHref = navbarStub?.map((object) => object?.href);

      // Slice the array till the active section
      const slicedNavbar = navbarStub?.slice(0, navbarStubHref?.indexOf(activeSection));

      // Find the width of the divs for all sliced sections
      const itemWidthArr = slicedNavbar?.map((item) =>
        Number(document.getElementById(item?.title || '')?.clientWidth)
      );

      // Calculate the total width from the left to the active section
      const totalWidth = itemWidthArr?.reduce((a, b) => a + b, 0);

      if (activeSection !== '' && elem.firstChild?.firstChild) {
        const sliderContent = elem.firstChild?.firstChild as HTMLDivElement;

        // Scroll to the active section
        sliderContent.scrollLeft = Number(totalWidth);

        // Try to find next and previous arrows
        const nextArrow = sliderContent.querySelector('.next-arrow') as HTMLDivElement;
        const preArrow = sliderContent.querySelector('.prev-arrow') as HTMLDivElement;

        if (scrollDownDirection) {
          nextArrow?.click(); // Ensure nextArrow exists and is clickable
        } else {
          preArrow?.click(); // Ensure preArrow exists and is clickable
        }
      }
    }
  };

  useEffect(() => {
    resetSlider();
  }, [activeSection]);

  const getSectionOffsets = (domArray: HTMLElement[]) => {
    const sectionOffsets = [];

    for (let i = 0; i < domArray.length; i += 1) {
      const currentOffset = (domArray[i] as HTMLElement).offsetTop;
      let nextOffset = null;

      if (i + 1 < domArray.length) {
        nextOffset = (domArray[i + 1] as HTMLElement).offsetTop;
      }
      if (nextOffset == null) {
        nextOffset =
          (domArray[i] as HTMLElement).offsetTop + (domArray[i] as HTMLElement).offsetHeight;
      }
      sectionOffsets.push([currentOffset, nextOffset]);
    }

    return sectionOffsets;
  };
  useEffect(() => {
    const AllSections = document.querySelectorAll('section[data-title]');
    const navStub: NavbarStub[] = Array.from(AllSections).map((ele) => {
      return {
        href: ele.getAttribute('id') || '',
        title: ele.getAttribute('data-title') || '',
      };
    });

    setNavbarStub(navStub);

    const navEvents = () => {
      if (screenWidth) {
        const nav = navbar.current;
        const pricing = pricingRef.current;
        const pricingDiv = nav?.querySelector('div');
        const navSection = nav?.querySelector('nav');
        if (navSection) {
          navSection?.classList.add('hide-scrollbar');
          navSection.style.width = `calc(100%-${pricingDiv?.clientWidth})`;
          navSection.style.overflow = 'scroll';
        }

        const { scrollY } = window;
        const overview: HTMLDivElement | null = document?.querySelector('#overview');
        if (
          Number(overview?.offsetTop) + Number(overview?.offsetHeight) &&
          scrollY >= (Number(overview?.offsetTop) + Number(overview?.offsetHeight) * 9) / 10
        ) {
          nav?.classList.remove('hidden');
          nav?.classList.add('flex');
          pricing?.classList.remove('hidden');
          pricing?.classList.add('flex');
          setNotificationState(true);
        } else {
          setNotificationState(false);
          nav?.classList.add('hidden');
          pricing?.classList.add('hidden');
        }
      }
      const sections = Array.from(
        document.querySelectorAll('section[data-title]')
      ) as HTMLElement[];
      const allSectionOffset = getSectionOffsets(sections);

      const index = allSectionOffset.findIndex(
        (data) => data[0] < window.scrollY + 70 && data[1] > window.scrollY + 70
      );

      if (index !== -1) {
        const id = sections[index]?.getAttribute('id');
        const query = `a[href*='${id}']`;
        const currentSection = navbar?.current?.querySelector(query);
        currentSection?.scrollIntoView();
        setActiveSection(id || '');
      }
    };

    if (typeof window !== 'undefined' && screenWidth) {
      window.addEventListener('scroll', () => navEvents());
    }
    return () => window.removeEventListener('scroll', navEvents);
  }, [screenWidth]);

  return (
    <StickyNavbar
      screenWidth={screenWidth}
      activeSection={activeSection}
      sliderRef={sliderRef}
      navbar={navbar}
      navbarStub={navbarStub}
      // pricingRef={pricingRef}
      darkTheme={darkTheme}
      isCampusEdgeCourseDescription={isCampusEdgeCourseDescription}
      planCourses={courseDetails?.courseMetaDetails?.coursePlansDetails || []}
      isOnlineDegree={isOnlineDegree}
    />
  );
};

export default NavHeaderSection;
